import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-2.5 border-b border-background-200/70 last:border-b-0 cursor-pointer"
      >
        <span className="text-xs font-bold text-primary-500 mt-0.5 whitespace-nowrap font-label">
          {String(article.id).padStart(2, '0')}
        </span>
        <span className="text-sm text-foreground-700 line-clamp-1 group-hover:text-primary-500 transition-colors flex-1">
          {article.title}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full font-label">
            热门
          </span>
        )}
        {article.isNew && (
          <span className="absolute top-3 right-3 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full font-label">
            最新
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="text-xs text-primary-500 font-medium mb-2 block font-label">
          {article.categoryName}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-[10px]"></i>
            {article.comments}
          </span>
        </div>
      </div>
    </Link>
  );
}